import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	HostBinding,
	HostListener,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild
} from "@angular/core";
import { Align, Collision, PopupComponent } from "@progress/kendo-angular-popup";
import * as _ from "lodash";
import { RtlService } from "../../../rtl/services/rtl.service";

export declare type Offset = {left:number, top:number};

export type TooltipDirection = "top" | "bottom"| "left" | "right";
type ArrowClass = "top-arrow" | "bottom-arrow" | "left-arrow" | "right-arrow";

@Component({
	selector: 'cp-tooltip',
	templateUrl: './tooltip.html',
	styleUrls: ['./tooltip.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class TooltipComponent implements OnInit, OnChanges, OnDestroy {
	@Input() label: string;
	@Input() labelParams: any;
	@Input() anchor: HTMLElement;
	@Input() direction: TooltipDirection;
	@Input() horizontalAlign: string = 'left';
	@Input() isReady: boolean = true;
	@Input() delay: number = 400;
	@Input() keepOpenWhenMouseIn: boolean;
	@Input() openOnClick: boolean = false;
	@Input() openOnContextMenu: boolean = false;

	@Input() openOnClickWithoutStopPropagation: boolean = false;
	// the default of tooltipAnchorHasChildElementsConsideredInClick should probably be true (or auto-calculated)
	// but we dont want to affect the existing code and cause a bug...
	@Input() tooltipAnchorHasChildElementsConsideredInClick: boolean = false;
	@Input() width: string;
	@Input() height: string;

	@Input() disposable: boolean = true;
	@Input() showCloseButton: boolean;

	//If no anchor is provided, the Popup uses the offset property value.
	@Input() offset: Offset;
	@Input() forceOpen: boolean;
	@Input() contentPadding: number = 5;
	@Input() tooltipPadding: boolean = true;
	@Input() alignCenter: boolean = true;
	@Input() verticalNoAlignBottom: boolean = false;

	@Input() disableClickOut: boolean = false;
	@Input() closeOnClick: boolean = false;
	@Input() forceArrowRightPosition: boolean = false;

	@Output() close = new EventEmitter();
	@Output() open = new EventEmitter();
	@Output() dispose = new EventEmitter();
	@Output() openedDisplayed = new EventEmitter();

	@ViewChild('popup',{static:true}) popup: PopupComponent;
	@ViewChild('popup', {read: ElementRef,static:false}) popupElement: ElementRef;

	public tooltipPaddingPx: number;

	public anchorAlign: Align;
	public popupAlign: Align;
	public arrowClass: ArrowClass;
	public collision: Collision;

	public isTooltipOpen: boolean;
	public isMouseLeave: boolean;
	public isMouseInPopup: boolean;
	public isFirstOpen: boolean;
	public checkedPosition:boolean;
	public tooltipID: string;


	public ARROW_WIDTH = 8;

	private right = "right";
	private left = "left";
	private rtl: boolean;

	constructor(private changeDetector: ChangeDetectorRef, private rtlService: RtlService) {
	}

	@HostBinding("class.overlay") get overlay() {
		return !this.disposable;
	}

	@HostListener('click', ['$event'])
	public onHostClick($event) {
		if (!this.disposable && $event.target.tagName.toLowerCase() === 'cp-tooltip') {
			$event.stopPropagation();
			this.dispose.emit();
		}
	}

	public ngOnInit(): void {
		this.rtl = this.rtlService.isRtl();

		if (this.rtl) {
			this.right = "left";
			this.left = "right";
		}

		this.tooltipPaddingPx = this.tooltipPadding ? 5: 0;
		this.collision = {vertical: "fit", horizontal: "fit"};
		this.setPopupAndAnchorAlignByDirection(this.direction);
		this.setArrowClassByDirection(this.direction);

		if (this.anchor && !this.forceOpen) {
			this.addAnchorEvents();
		} else {
			this.isTooltipOpen = true;
		}

		this.isFirstOpen = true;
		this.checkedPosition=false;
		this.tooltipID = "tooltip" + _.uniqueId();
	}

	// use when the same tooltip opens with different contents and we need the arrow to be recalculated
	public reset(){
		this.isFirstOpen=true;
		this.checkedPosition=false;
	}
		
	ngOnChanges(changes:SimpleChanges) {
		if (changes['isReady'] && !changes['isReady'].isFirstChange() && this.isTooltipOpen &&
			changes['isReady'].currentValue && !changes['isReady'].previousValue) {
			setTimeout(() => {
				if (this.haveCollision()) {
					this.flip();
				}
				this.setArrowPosition();
				this.checkedPosition=true;
				this.changeDetector.markForCheck();
			}, 0);
		}
	}

	public ngOnDestroy(): void {
		this.removeAnchorEvents();
	}

	public closeTooltip() {
		this.isTooltipOpen = false;
		this.changeDetector.markForCheck();
	}

	public openTooltip() {
		this.isTooltipOpen = true;
		this.changeDetector.markForCheck();
	}

	public setArrowClassByDirection(direction: TooltipDirection) {
		switch (direction) {
			case "top":
				this.arrowClass = "bottom-arrow";
				break;
			case "right":
				this.arrowClass = "left-arrow";
				break;
			case "left":
				this.arrowClass = "right-arrow";
				break;
			default:
				this.arrowClass = "top-arrow";
		}
	}

	public setPopupAndAnchorAlignByDirection(direction: TooltipDirection) {
		let horizontalAlign = this.alignCenter ? "center" : (this.horizontalAlign === 'right' ? this.right : this.left);
		let verticalAlign = this.alignCenter ? "center" : this.verticalNoAlignBottom ?"top":"bottom";

		switch (direction) {
			case "top":
				this.anchorAlign = <Align> {horizontal: horizontalAlign, vertical: "top"};
				this.popupAlign = <Align> {horizontal: horizontalAlign, vertical: "bottom"};
				break;
			case "right":
				this.anchorAlign = <Align>{horizontal: this.right, vertical: verticalAlign};
				this.popupAlign = <Align>{horizontal: this.left, vertical: verticalAlign};
				break;
			case "left":
				this.anchorAlign = <Align>{horizontal: this.left, vertical: verticalAlign};
				this.popupAlign = <Align>{horizontal: this.right, vertical: verticalAlign};
				break;
			default:
				this.anchorAlign = <Align>{horizontal: horizontalAlign, vertical: "bottom"};
				this.popupAlign = <Align>{horizontal: horizontalAlign, vertical: "top"};
		}
	}


	public setArrowPosition(recalc?: boolean) {
		if (this.popupElement) {
			let anchorCenter;
			if (this.anchor) {
				//Calculate the center of the anchor with the left padding and without the right padding
				let anchorRects = this.anchor.getClientRects()[0];
				let leftBeforeOffset;
				if (this.alignCenter){
					// it is not likely that someone would require the right/left padding adjustments
					// for a centeralized tooltip. we would prefer to avoid doing unnecessary computed style checks
					// even though there is a client-rects evalaution anyway
					leftBeforeOffset = anchorRects.left + anchorRects.width / 2;
				}else {
					let compusterStyle=window.getComputedStyle(this.anchor, null);
					let paddingRight = compusterStyle.getPropertyValue('padding-right');
					let paddingLeft = compusterStyle.getPropertyValue('padding-left');
					leftBeforeOffset = anchorRects.left + anchorRects.width / 2 + this.pixelToNumber(paddingLeft) / 2 - this.pixelToNumber(paddingRight);
				}

				anchorCenter = {
					// keep using the left even for rtl since the right does not provide the absolution correct location
					left: leftBeforeOffset + (this.offset && this.offset.left ? this.offset.left : 0),
					top: anchorRects.top + (anchorRects.height / 2) + (this.offset && this.offset.top ? this.offset.top : 0)
				};
			}else{
				anchorCenter = this.offset;
			}

			let positionDirection: string;
			let positionPixelsBefore: number;
			let positionPixelsAfter: number;
			// change the minimum edge to 0
			let edge = 0;
			let popupRects = this.popupElement.nativeElement.getClientRects()[0];
			if (this.arrowClass === "top-arrow" || this.arrowClass === "bottom-arrow") {
				// always use left regardless of rtl, except cases when tooltip dynamically grows left but anchor remains right
				positionDirection = (this.forceArrowRightPosition && !this.rtl) ? "right" : "left";
				let popupWidth = popupRects.width - this.tooltipPaddingPx * 2;
				positionPixelsBefore = Math.max(Math.min(
						Math.ceil(anchorCenter.left - popupRects.left - this.tooltipPaddingPx - this.ARROW_WIDTH/2 - 3), popupWidth - edge),
					edge);				
				positionPixelsAfter=positionPixelsBefore+1;
			} else {
				positionDirection = "top";
				let popupHeight =  popupRects.height - this.tooltipPaddingPx * 2;
				positionPixelsBefore = Math.max(Math.min(Math.ceil(anchorCenter.top - popupRects.top - this.tooltipPaddingPx), popupHeight - edge), edge);
				positionPixelsAfter = positionPixelsBefore;
			}

			if (recalc) {
				(this.popupElement.nativeElement.parentElement).find("style").remove();
			}

			let styleElem = document.createElement("style");
			styleElem.innerText="." +
				this.arrowClass + '.' + this.tooltipID + ":before{" + positionDirection + ":" + positionPixelsBefore + "px !important;}."+
				this.arrowClass + '.' + this.tooltipID + ":after{" + positionDirection + ":" + positionPixelsAfter + "px !important;}";
			(this.popupElement.nativeElement.parentElement).append(styleElem);
		}
	}

	/*public addAnchorEvents() {
		if (this.anchor && this.anchor.addEventListener) {

			if (this.openOnClick){
				this.anchor.addEventListener("click", this.toggleToolTip);
			} else {
				this.anchor.addEventListener("mouseenter", this.onAnchorMouseEnter);
				this.anchor.addEventListener("mouseleave", this.onAnchorMouseLeave);

				if (this.closeOnClick) {
					this.anchor.addEventListener("click", this.onAnchorCloseClick);
				}
			}
			if (this.openOnContextMenu){
				this.anchor.addEventListener("contextmenu", this.toggleToolTip);
			}
		}
	}*/

	public addAnchorEvents() {
		if (this.anchor && this.anchor.addEventListener) {

			if (!(this.openOnClick || this.openOnContextMenu)){
				this.anchor.addEventListener("mouseenter", this.onAnchorMouseEnter);
				this.anchor.addEventListener("mouseleave", this.onAnchorMouseLeave);

				if (this.closeOnClick) {
					this.anchor.addEventListener("click", this.onAnchorCloseClick);
				}
			}

			if (this.openOnClick){
				this.anchor.addEventListener("click", this.toggleToolTip);

			}
			if (this.openOnContextMenu){
				this.anchor.addEventListener("contextmenu", this.toggleToolTip);
			}
		}
	}



	/*public removeAnchorEvents() {
		if (this.anchor && this.anchor.removeEventListener) {
			if (this.openOnClick){
				this.anchor.removeEventListener("click", this.toggleToolTip);
			} else{
				this.anchor.removeEventListener("mouseenter", this.onAnchorMouseEnter);
				this.anchor.removeEventListener("mouseleave", this.onAnchorMouseLeave);

				if (this.closeOnClick) {
					this.anchor.removeEventListener("click", this.onAnchorCloseClick);
				}
			}
			if (this.openOnContextMenu){
				this.anchor.removeEventListener("contextmenu", this.toggleToolTip);
			}
		}
	}*/
	public removeAnchorEvents() {
		if (this.anchor && this.anchor.removeEventListener) {
			if (!(this.openOnClick || this.openOnContextMenu)){
				this.anchor.removeEventListener("mouseenter", this.onAnchorMouseEnter);
				this.anchor.removeEventListener("mouseleave", this.onAnchorMouseLeave);

				if (this.closeOnClick) {
					this.anchor.removeEventListener("click", this.onAnchorCloseClick);
				}
			}

			if (this.openOnClick){
				this.anchor.removeEventListener("click", this.toggleToolTip);
			}
			if (this.openOnContextMenu){
				this.anchor.removeEventListener("contextmenu", this.toggleToolTip);
			}
		}
	}

	public onAnchorMouseEnter = (event) => {
		this.isMouseLeave = false;
		setTimeout(() => {
			if (!this.isMouseLeave) {
				this.isTooltipOpen = true;
				this.changeDetector.markForCheck();
			}
		}, this.delay);
	};

	public onAnchorCloseClick = (event) => {
		this.onClose(event);
		this.changeDetector.markForCheck();
	}


	public toggleToolTip = (event) => {
		this.isTooltipOpen = !this.isTooltipOpen;
		this.changeDetector.markForCheck();
		if (!this.openOnClickWithoutStopPropagation){
			event.stopPropagation();
		}
		if (this.openOnContextMenu){
			event.preventDefault();
		}
	};

	public onAnchorMouseLeave = (event) => {
		setTimeout(() => { // time for the gap between the anchor and the tooltip
			this.isMouseLeave = true;

			if (!this.keepOpenWhenMouseIn || !this.isMouseInPopup) {
				this.onClose(event);
			}

			this.changeDetector.markForCheck();
		}, 50);
	};

	public onPopupMouseEnter() {
		this.isMouseInPopup = true;
	}

	public onPopupMouseLeave(event) {
		this.isMouseInPopup = false;
		if (this.isMouseLeave && this.isTooltipOpen) {
			this.onClose(event);
		}
	}

	public onClose(event) {
		this.isTooltipOpen = false;
		this.close.emit(event);
	}

	public onOpen(event) {
		if (this.isFirstOpen) {
			this.isFirstOpen = false;
			// depending on the DOM the tooltip needs to set itself on the correct location so we will wait..
			setTimeout(()=> {
				if (this.isReady) {
					this.checkedPosition=true;
					if (this.haveCollision()) {
						this.flip();
					}
					this.setArrowPosition();
					this.changeDetector.markForCheck();
					setTimeout(()=> {
						// the UI is now available on the DOM after the detection
						this.openedDisplayed.emit();
					});
				}
			});
		}

		this.open.emit(event);
	}

	public haveCollision(): boolean {
		//The collision set to be "fit" so if the popup on the edge I suppose that was a collision.
		let popupRects = this.popupElement.nativeElement.getClientRects()[0];

		switch (this.direction) {
			case "top":
				return popupRects.top <= 0;
			case this.right:
				return Math.ceil(popupRects.left + popupRects.width) >= document.body.clientWidth;
			case this.left:
				return popupRects.left <= 0;
			default:
				return Math.ceil(popupRects.top + popupRects.height) >= document.body.clientHeight;
		}
	}

	public flip() {
		let newDirection: TooltipDirection;

		switch (this.direction) {
			case "top":
				newDirection = "bottom";
				break;
			case "right":
				newDirection = "left";
				break;
			case "left":
				newDirection = "right";
				break;
			default:
				newDirection = "top";
		}

		this.setPopupAndAnchorAlignByDirection(newDirection);
		this.setArrowClassByDirection(newDirection);
	}

	public onClickOut(event) {
		if (this.openOnClick || this.openOnContextMenu){
			if (this.isTooltipOpen){
				if ((event.target === this.anchor) || (this.tooltipAnchorHasChildElementsConsideredInClick && this.anchor.contains(event.target))){
					return;
				}
				this.toggleToolTip(event);
			}
		}
	}

	private pixelToNumber(valueWithPx:string):number{
		return +(valueWithPx.substring(0,valueWithPx.indexOf("px")));
	}
}